import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { BusinessModelCreationModal, FormData as ModalFormData } from '@/components/business-model/BusinessModelCreationModal';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { AiDetailPanel } from '@/components/shared/AiDetailPanel';
import { useAiPagePermissions } from '@/hooks/useAiPagePermissions';
import { projectsApi, businessModelsApi, positioningsApi, marketAnalysesApi } from '@/utils/api-pb';
import { pb } from '@/utils/pocketbase';
import type { Project, BusinessModel, Positioning, MarketAnalysis } from '@/utils/pocketbase';
import { API_URL } from '@/utils/config';


import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
const FREE_BUSINESS_MODEL_LIMIT = 0;

export default function BusinessModelPage() {
  const { t } = useTranslation('business-model');
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [businessModels, setBusinessModels] = useState<BusinessModel[]>([]);
  const [positionings, setPositionings] = useState<Positioning[]>([]);
  const [marketAnalyses, setMarketAnalyses] = useState<MarketAnalysis[]>([]);
  const [selectedBusinessModel, setSelectedBusinessModel] = useState<BusinessModel | null>(null);

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [businessModelToDelete, setBusinessModelToDelete] = useState<string | null>(null);

  const [formData, setFormData] = useState<ModalFormData>({
    project_id: '',
    positioning_id: '',
    market_analysis_id: '',
    revenue_hint: '',
    target_market_size: '',
    comment: ''
  });

  // Permissions
  const { isFree, canCreateNew, canEdit, canDelete } = useAiPagePermissions(businessModels.length, FREE_BUSINESS_MODEL_LIMIT);

  // Tooltip state for create button
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string, type: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select business model (prefer recordId from query param, fallback to first)
  useEffect(() => {
    if (businessModels.length > 0 && !selectedBusinessModel) {
      const targetId = searchParams.get('recordId');
      const target = targetId ? businessModels.find(m => m.id === targetId) : null;
      setSelectedBusinessModel(target ?? businessModels[0] ?? null);
    }
  }, [businessModels, selectedBusinessModel, searchParams]);

  const loadData = async () => {
    try {
      setIsInitialLoading(true);
      const results = await Promise.allSettled([
        projectsApi.list(),
        businessModelsApi.list(),
        positioningsApi.list(),
        marketAnalysesApi.list(),
      ]);

      if (results[0]?.status === 'fulfilled') setProjects(results[0].value);
      else console.error('Error loading projects:', results[0]?.reason);

      if (results[1]?.status === 'fulfilled') setBusinessModels(results[1].value);
      else console.error('Error loading business models:', results[1]?.reason);

      if (results[2]?.status === 'fulfilled') setPositionings(results[2].value);
      else console.error('Error loading positionings:', results[2]?.reason);

      if (results[3]?.status === 'fulfilled') setMarketAnalyses(results[3].value);
      else console.error('Error loading market analyses:', results[3]?.reason);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmitModal = async (modalFormData: ModalFormData) => {
    setIsLoading(true);

    try {
      const selectedProject = projects.find(p => p.id === modalFormData.project_id);

      const descriptionParts: string[] = [];
      if (selectedProject?.name) descriptionParts.push(`Продукт: ${selectedProject.name}`);
      if (selectedProject?.description) descriptionParts.push(`Описание: ${selectedProject.description}`);
      if (selectedProject?.market) descriptionParts.push(`Рынок: ${selectedProject.market}`);

      const productDescription = descriptionParts.join('\n');

      const response = await fetch(`${API_URL}/api/ai/business-model`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({
          replaceId: isEditing ? selectedBusinessModel?.id : undefined,
          projectId: modalFormData.project_id,
          productDescription,
          positioningId: modalFormData.positioning_id || undefined,
          marketAnalysisId: modalFormData.market_analysis_id || undefined,
          revenueHint: modalFormData.revenue_hint || undefined,
          targetMarketSize: modalFormData.target_market_size || undefined,
          comment: modalFormData.comment || undefined,
          lang,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || t('toast.createError'));
      }

      setShowEditModal(false);
      setShowNewModal(false);
      setFormData({
        project_id: '',
        positioning_id: '',
        market_analysis_id: '',
        revenue_hint: '',
        target_market_size: '',
        comment: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating/updating business model:', error);
      const errorMessage = error instanceof Error ? error.message : t('toast.createError');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await businessModelsApi.delete(id);
      setShowDeleteConfirm(false);
      setBusinessModelToDelete(null);
      if (selectedBusinessModel?.id === id) {
        setSelectedBusinessModel(null);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting business model:', error);
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreateNew = () => {
    if (!canCreateNew) return;

    setFormData({
      project_id: '',
      positioning_id: '',
      market_analysis_id: '',
      revenue_hint: '',
      target_market_size: '',
      comment: ''
    });
    setIsEditing(false);
    setSelectedBusinessModel(null);
    setShowNewModal(true);
  };

  const handleEditBusinessModel = (businessModel: BusinessModel) => {
    if (!canEdit) return;

    setFormData({
      project_id: businessModel.project || '',
      positioning_id: businessModel.positioning || businessModel.input_data?.positioningId || '',
      market_analysis_id: businessModel.market_analysis || businessModel.input_data?.marketAnalysisId || '',
      revenue_hint: businessModel.input_data?.revenueHint || '',
      target_market_size: businessModel.input_data?.targetMarketSize || '',
      comment: businessModel.input_data?.comment || ''
    });
    setSelectedBusinessModel(businessModel);
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleSelectBusinessModel = (businessModel: BusinessModel) => {
    setSelectedBusinessModel(businessModel);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowNewModal(false);
    setIsEditing(false);
  };

  // Get business model title based on product name
  const getBusinessModelTitle = (businessModel: BusinessModel): string => {
    const project = projects.find(p => p.id === businessModel.project);
    return t('itemTitle', { projectName: project?.name || t('defaultProjectName') });
  };

  // Count business models per project for numbering (chronological: oldest = 1)
  const businessModelNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    businessModels.forEach(businessModel => {
      const projectId = businessModel.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    // Data is already sorted by created (oldest first)
    const projectIndexes: Record<string, number> = {};
    businessModels.forEach(businessModel => {
      const projectId = businessModel.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[businessModel.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1
      };
    });

    return numbers;
  }, [businessModels]);

  const getBusinessModelListTitle = (businessModel: BusinessModel): string => {
    const numberInfo = businessModelNumbers[businessModel.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return `${getBusinessModelTitle(businessModel)}${shouldShowNumber ? ` ${displayNum}` : ''}`;
  };

  if (!isInitialLoading && isFree) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <SubscriptionLimitBanner
          title={t('paidBanner.title')}
          description={t('paidBanner.description')}
        />
      </>
    );
  }

  return (
    <>{isInitialLoading ? (
          /* Initial Loading State */
          <div className="flex flex-col items-center justify-center gap-[16px] py-[80px]">
            <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] text-center">
              {t('title')}
            </h2>
            <p className="font-segoe text-[16px] leading-[24px] text-[#565858] text-center">
              {t('loading')}
            </p>
            <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : businessModels.length === 0 ? (
          /* Empty State */
          <><PageHeader title={t('title')} description={t('description')} />
          <div className="flex flex-col gap-[24px]">
            <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
              <p>
                {t('empty.text')}
              </p>
            </div>

            <button
              onClick={handleCreateNew}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new', type: 'create' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative inline-block"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                } rounded-[16px] px-[24px] py-[12px] transition-colors inline-block`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-new' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.freeLimitCreate')}
                  </p>
                </div>
              )}
            </button>
          </div>
          </>
        ) : (
          /* List with Selected Business Model */
          <div className="flex flex-col lg:flex-row gap-[24px]">
            {/* Left Sidebar - Business Models List */}
            <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
              <PageHeader title={t('title')} description={t('description')} />
              <AiItemsList
                items={businessModels}
                selectedId={selectedBusinessModel?.id ?? null}
                getItemTitle={getBusinessModelListTitle}
                onSelect={handleSelectBusinessModel}
                isItemDisabled={(_item, index) => isFree && index > 0}
                tooltipMessage={t('tooltip.paidOnly')}
              />

              {/* Create New Button */}
              <button
                onClick={handleCreateNew}
                disabled={!canCreateNew}
                onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new-sidebar', type: 'create' })}
                onMouseLeave={() => setTooltipVisible(null)}
                className="relative"
              >
                <div className={`${canCreateNew
                  ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                  : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                  } rounded-[16px] px-[24px] py-[12px] transition-colors`}>
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    {t('newButton')}
                  </p>
                </div>

                {tooltipVisible?.id === 'create-new-sidebar' && !canCreateNew && (
                  <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                    <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                      {t('tooltip.freeLimitCreate')}
                    </p>
                  </div>
                )}
              </button>

            </div>

            {/* Right Content - Selected Business Model */}
            {selectedBusinessModel && (
              <AiDetailPanel
                key={selectedBusinessModel.id}
                title={getBusinessModelTitle(selectedBusinessModel)}
                content={selectedBusinessModel.generated_text}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => handleEditBusinessModel(selectedBusinessModel)}
                onDelete={() => {
                  setBusinessModelToDelete(selectedBusinessModel.id);
                  setShowDeleteConfirm(true);
                }}
                editButtonText={t('detail.editButton')}
                actionButtons={[
                  { text: t('detail.actionMvpPlan'), onClick: () => navigate('/mvp-plan') }
                ]}
                pdfExportData={selectedBusinessModel.generated_text ? {
                  title: getBusinessModelTitle(selectedBusinessModel),
                  content: selectedBusinessModel.generated_text,
                  artifactType: 'business_model',
                  artifactTypeLabel: t('detail.pdfLabel'),
                  projectName: projects.find(p => p.id === selectedBusinessModel.project)?.name || t('defaultProjectName'),
                  date: selectedBusinessModel.created,
                } : undefined}
                tooltipEditText={t('tooltip.paidOnly')}
                tooltipDeleteText={t('tooltip.paidOnly')}
                emptyText={t('detail.emptyText')}
              />
            )}
          </div>
        )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setBusinessModelToDelete(null);
        }}
        onConfirm={() => businessModelToDelete && handleDelete(businessModelToDelete)}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.deleteCancel')}
      />

      {/* Business Model Modal (Create/Edit) */}
      <BusinessModelCreationModal
        isOpen={showNewModal || showEditModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        projects={projects}
        marketAnalyses={marketAnalyses}
        positionings={positionings}
        initialData={formData}
        isEditing={isEditing}
        isLoading={isLoading}
      />
    </>
  );
}
